import { inject, Injectable } from '@angular/core';
import {
  ConnectedDataSource,
  ConnectedDataSourceService,
} from '@tiltgroup/web-components';
import {
  CarrierFilters,
  ContactFilters,
  ContactType,
  FacilityFilters,
  SqlOperator,
  TeamFilters,
  UserFilters,
} from '@graphql-types';
import { CarriersQueriesService } from '@services/queries/services/carriers-queries.service';
import { LighthouseQueriesService } from '@services/queries/services/lighthouse-queries.service';
import { TeamsQueriesService } from '@services/queries/services/teams-queries.service';

@Injectable({
  providedIn: 'root',
})
export class ConnectedDataSourcesService {
  private _lighthouseQueriesService = inject(LighthouseQueriesService);
  private _teamsQueriesService = inject(TeamsQueriesService);
  private _carriersQueriesService = inject(CarriersQueriesService);
  private _cds = inject(ConnectedDataSourceService);

  get facilitiesDataSource(): ConnectedDataSource {
    return this._cds.initAutocompleteDataSource((pageRequest, query) => {
      return this._lighthouseQueriesService.getFacilities({
        page: pageRequest.page as number,
        size: pageRequest.size as string,
        ...(query?.searchTerm && {
          where: {
            column: FacilityFilters.Name,
            operator: SqlOperator.Ilike,
            value: query?.searchTerm,
          },
        }),
      });
    });
  }

  get userDataSource(): ConnectedDataSource {
    return this._cds.initDataSource((pageRequest, query) => {
      return this._lighthouseQueriesService.getUsers({
        page: pageRequest.page as number,
        size: pageRequest.size as string,
        ...(query?.searchTerm && {
          where: {
            column: UserFilters.FullName,
            operator: SqlOperator.Ilike,
            value: query?.searchTerm,
          },
        }),
      });
    });
  }

  get teamsDataSource(): ConnectedDataSource {
    return this._cds.initDataSource((pageRequest, query) => {
      return this._teamsQueriesService.getTeams({
        page: pageRequest.page as number,
        size: pageRequest.size as string,
        ...(query?.searchTerm && {
          where: {
            column: TeamFilters.Name,
            operator: SqlOperator.Ilike,
            value: query?.searchTerm,
          },
        }),
      });
    });
  }

  /**
   * Carrier lookup for the bill of lading form: the selected carrier's details
   * (name, MC#, USDOT#) get printed on the generated PDF.
   */
  get carrierForBillOfLadingDataSource(): ConnectedDataSource {
    return this._cds.initAutocompleteDataSource((pageRequest, query) => {
      return this._carriersQueriesService.getCarriers({
        page: pageRequest.page as number,
        size: pageRequest.size as string,
        ...(query?.searchTerm && {
          where: {
            column: CarrierFilters.Name,
            operator: SqlOperator.Ilike,
            value: query?.searchTerm,
          },
        }),
      });
    });
  }

  get facilityManagerContactsDataSource(): ConnectedDataSource {
    return this._cds.initAutocompleteDataSource((pageRequest, query) => {
      return this._lighthouseQueriesService.getContacts({
        page: pageRequest.page as number,
        size: pageRequest.size as string,
        where: {
          column: ContactFilters.Type,
          operator: SqlOperator.Eq,
          // Raw SQL comparison, so this needs the stored value ("facility_manager"),
          // not the GraphQL enum name.
          value: ContactType.FacilityManager.toLowerCase(),
          ...(query?.searchTerm && {
            OR: [
              {
                column: ContactFilters.FullName,
                operator: SqlOperator.Ilike,
                value: query?.searchTerm,
              },
              {
                column: ContactFilters.EmailAddress,
                operator: SqlOperator.Ilike,
                value: query?.searchTerm,
              },
              {
                column: ContactFilters.PhoneNumber,
                operator: SqlOperator.Ilike,
                value: query?.searchTerm,
              },
            ],
          }),
        },
      });
    });
  }
}
